import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import ArticleContent from "@/pages/detail/components/ArticleContent";
import DetailActions from "@/pages/detail/components/DetailActions";
import { articles, articleMap } from "@/mocks/articles";
import { categoryMap } from "@/mocks/categories";
import { useSeo } from "@/hooks/useSeo";
import { useFavorites } from "@/hooks/useFavorites";
import { useHistory } from "@/hooks/useHistory";

export default function Detail() {
  const { id } = useParams();
  const article = id ? articleMap[id] : undefined;
  const { favorites, toggle, isFavorite } = useFavorites();
  const { record } = useHistory();
  const [lightbox, setLightbox] = useState<string | null>(null);

  useEffect(() => {
    if (id) record(id);
  }, [id, record]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const related = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((item) => item.id !== article.id && (item.category === article.category || item.topicId === article.topicId))
      .slice(0, 4);
  }, [article]);

  useSeo({
    title: article ? `${article.title} - 黑人文化资讯` : "文章未找到 - 黑人文化资讯",
    description: article ? article.summary : "未找到对应文章，请返回首页浏览更多黑人文化资讯。",
    keywords: article ? [...article.tags, article.categoryName, "黑人文化资讯"].join(",") : "黑人文化资讯",
  });

  if (!article) {
    return (
      <div className="mx-auto max-w-content px-4 md:px-8 py-20 text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center text-foreground-400">
          <i className="ri-file-unknow-line text-3xl" />
        </div>
        <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-900">文章不存在</h1>
        <p className="mt-3 text-sm text-foreground-500">你访问的文章可能已被移动或删除。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const category = categoryMap[article.category];
  const favoriteOn = isFavorite(article.id);
  const recentFav = favorites.slice(0, 4);

  return (
    <div className="mx-auto max-w-content px-4 md:px-8 py-8 md:py-12">
      <div className="bg-background-50 rounded-lg border border-background-200 px-4 py-3 mb-8">
        <Breadcrumb
          items={[
            { label: "分类", to: "/" },
            { label: article.categoryName, to: `/category/${article.category}` },
            { label: article.title },
          ]}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <article className="lg:col-span-8">
          <Link
            to={`/category/${article.category}`}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className={category ? category.icon : "ri-price-tag-3-line"} />
            {article.categoryName}
          </Link>

          <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-snug">
            {article.title}
          </h1>

          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-500 pb-6 border-b border-background-200">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-3-line" />
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" />
              {article.date}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line" />
              {article.readTime} 分钟阅读
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line" />
              {article.views.toLocaleString()} 阅读
            </span>
          </div>

          <div className="mt-7 w-full h-64 md:h-[420px] rounded-lg overflow-hidden border border-background-200">
            <img
              src={article.cover}
              alt={article.title}
              title={`${article.title} - 黑人文化资讯`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <p className="mt-7 text-base md:text-lg text-foreground-800 leading-relaxed font-medium">
            {article.summary}
          </p>

          <div className="mt-7">
            <ArticleContent blocks={article.content} onImageClick={setLightbox} />
          </div>

          <div className="mt-10 pt-6 border-t border-background-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/category/${article.category}`}
                  className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {tag}
                </Link>
              ))}
            </div>
            <DetailActions
              title={article.title}
              favorite={favoriteOn}
              onToggleFavorite={() => toggle(article.id)}
            />
          </div>

          {related.length > 0 && (
            <div className="mt-14">
              <div className="flex items-center gap-2 mb-6">
                <i className="ri-links-line text-accent-600 text-lg" />
                <h2 className="font-heading text-xl font-bold text-foreground-900">相关文章</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {related.map((item) => (
                  <ArticleCard key={item.id} article={item} variant="horizontal" />
                ))}
              </div>
            </div>
          )}
        </article>

        <aside className="lg:col-span-4 space-y-6">
          <div className="bg-background-100/70 rounded-lg border border-background-200 p-5 md:p-6">
            <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">文章信息</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center justify-between gap-3">
                <span className="text-foreground-500">分类</span>
                <Link to={`/category/${article.category}`} className="text-foreground-800 hover:text-primary-600 transition-colors cursor-pointer">
                  {article.categoryName}
                </Link>
              </li>
              <li className="flex items-center justify-between gap-3">
                <span className="text-foreground-500">作者</span>
                <span className="text-foreground-800">{article.author}</span>
              </li>
              <li className="flex items-center justify-between gap-3">
                <span className="text-foreground-500">发布日期</span>
                <span className="text-foreground-800">{article.date}</span>
              </li>
              <li className="flex items-center justify-between gap-3">
                <span className="text-foreground-500">阅读量</span>
                <span className="text-foreground-800">{article.views.toLocaleString()}</span>
              </li>
            </ul>
            {article.topicId && (
              <Link
                to={`/topic/${article.topicId}`}
                className="mt-5 flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-bookmark-3-line" />
                查看所属专题
              </Link>
            )}
          </div>

          <div className="bg-background-50 rounded-lg border border-background-200 p-5 md:p-6">
            <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">我的收藏</h3>
            {recentFav.length > 0 ? (
              <div className="space-y-1">
                {recentFav.map((favId) => {
                  const fav = articleMap[favId];
                  if (!fav) return null;
                  return (
                    <Link
                      key={favId}
                      to={`/detail/${favId}`}
                      className="group block py-2.5 border-b border-background-200 last:border-0 cursor-pointer"
                    >
                      <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                        {fav.title}
                      </span>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-foreground-500 leading-relaxed">
                还没有收藏任何文章。点击文章上方的「收藏」按钮，即可保存在本机浏览器中。
              </p>
            )}
          </div>

          <div className="bg-primary-600 rounded-lg p-5 md:p-6 text-background-50">
            <h3 className="font-heading text-lg font-semibold mb-3">探索更多</h3>
            <p className="text-sm text-primary-100 leading-relaxed mb-4">
              通过分类与专题，系统化地浏览黑人文化的多维内容。
            </p>
            <Link
              to="/search"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-accent-500 text-secondary-950 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-search-line" />
              前往搜索
            </Link>
          </div>
        </aside>
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-[70] bg-foreground-950/90 flex items-center justify-center p-4 animate-soft-in"
          onClick={() => setLightbox(null)}
        >
          <button
            type="button"
            aria-label="关闭"
            onClick={() => setLightbox(null)}
            className="absolute top-5 right-5 w-11 h-11 flex items-center justify-center rounded-full bg-background-50/15 text-background-50 hover:bg-background-50/25 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl" />
          </button>
          <img
            src={lightbox}
            alt="大图预览"
            className="max-w-full max-h-[85vh] rounded-lg object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}